"use client";

import {
  createContext,
  startTransition,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode
} from "react";
import type { Product } from "@/lib/catalog";
import type { CustomerType } from "@/lib/customer-auth";

type CustomerSnapshot = {
  userId: number;
  email: string;
  customerType: CustomerType;
  fullName: string;
  companyName: string;
};

type FavoriteToggleResult = {
  ok: boolean;
  active?: boolean;
  authRequired?: boolean;
};

type UserListsContextValue = {
  isReady: boolean;
  customer: CustomerSnapshot | null;
  isAuthenticated: boolean;
  favorites: Product[];
  compareItems: Product[];
  favoriteCount: number;
  compareCount: number;
  isFavorite: (slug: string) => boolean;
  isCompared: (slug: string) => boolean;
  toggleFavorite: (product: Product) => Promise<FavoriteToggleResult>;
  removeFavorite: (slug: string) => Promise<void>;
  clearFavorites: () => Promise<void>;
  toggleCompare: (product: Product) => { ok: boolean; added: boolean };
  removeCompare: (slug: string) => void;
  clearCompare: () => void;
};

const COMPARE_STORAGE_KEY = "bitstone_compare_v1";
const COMPARE_LIMIT = 3;

const UserListsContext = createContext<UserListsContextValue | null>(null);

function readStoredProducts(key: string) {
  if (typeof window === "undefined") {
    return [];
  }

  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw) as Product[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeStoredProducts(key: string, items: Product[]) {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.setItem(key, JSON.stringify(items));
}

function upsertProduct(items: Product[], product: Product) {
  const existingIndex = items.findIndex((item) => item.slug === product.slug);
  if (existingIndex === -1) {
    return [...items, product];
  }

  const nextItems = [...items];
  nextItems[existingIndex] = product;
  return nextItems;
}

async function syncFavoriteRequest(method: "POST" | "DELETE", body?: Record<string, unknown>) {
  const response = await fetch("/api/account/favorites", {
    method,
    headers: {
      "Content-Type": "application/json"
    },
    body: body ? JSON.stringify(body) : undefined
  });

  if (response.status === 401) {
    return {
      ok: false as const,
      unauthorized: true,
      favorites: [] as Product[]
    };
  }

  const payload = (await response.json()) as {
    ok?: boolean;
    active?: boolean;
    favorites?: Product[];
  };

  return {
    ok: Boolean(payload.ok && Array.isArray(payload.favorites)),
    active: payload.active,
    unauthorized: false,
    favorites: Array.isArray(payload.favorites) ? payload.favorites : []
  };
}

export function UserListsProvider({
  children,
  initialCustomer,
  initialFavorites
}: {
  children: ReactNode;
  initialCustomer: CustomerSnapshot | null;
  initialFavorites: Product[];
}) {
  const [customer, setCustomer] = useState<CustomerSnapshot | null>(initialCustomer);
  const [favorites, setFavorites] = useState<Product[]>(initialFavorites);
  const [compareItems, setCompareItems] = useState<Product[]>([]);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    setCustomer(initialCustomer);
    setFavorites(initialFavorites);
  }, [initialCustomer, initialFavorites]);

  useEffect(() => {
    startTransition(() => {
      setCompareItems(readStoredProducts(COMPARE_STORAGE_KEY));
      setIsReady(true);
    });
  }, []);

  useEffect(() => {
    if (!isReady) {
      return;
    }

    writeStoredProducts(COMPARE_STORAGE_KEY, compareItems);
  }, [compareItems, isReady]);

  const value = useMemo<UserListsContextValue>(() => {
    return {
      isReady,
      customer,
      isAuthenticated: Boolean(customer),
      favorites,
      compareItems,
      favoriteCount: favorites.length,
      compareCount: compareItems.length,
      isFavorite(slug) {
        return favorites.some((item) => item.slug === slug);
      },
      isCompared(slug) {
        return compareItems.some((item) => item.slug === slug);
      },
      async toggleFavorite(product) {
        if (!customer) {
          return { ok: false, authRequired: true };
        }

        const result = await syncFavoriteRequest("POST", { slug: product.slug });
        if (result.unauthorized) {
          setCustomer(null);
          setFavorites([]);
          return { ok: false, authRequired: true };
        }

        if (result.ok) {
          setFavorites(result.favorites);
        }

        return {
          ok: result.ok,
          active: result.active
        };
      },
      async removeFavorite(slug) {
        if (!customer) {
          return;
        }

        const result = await syncFavoriteRequest("DELETE", { slug });
        if (result.unauthorized) {
          setCustomer(null);
          setFavorites([]);
          return;
        }

        if (result.ok) {
          setFavorites(result.favorites);
        }
      },
      async clearFavorites() {
        if (!customer) {
          return;
        }

        const result = await syncFavoriteRequest("DELETE", { clear: true });
        if (result.unauthorized) {
          setCustomer(null);
          setFavorites([]);
          return;
        }

        if (result.ok) {
          setFavorites(result.favorites);
        }
      },
      toggleCompare(product) {
        const exists = compareItems.some((item) => item.slug === product.slug);

        if (exists) {
          setCompareItems((currentItems) =>
            currentItems.filter((item) => item.slug !== product.slug)
          );
          return { ok: true, added: false };
        }

        if (compareItems.length >= COMPARE_LIMIT) {
          return { ok: false, added: false };
        }

        setCompareItems((currentItems) => upsertProduct(currentItems, product));
        return { ok: true, added: true };
      },
      removeCompare(slug) {
        setCompareItems((currentItems) => currentItems.filter((item) => item.slug !== slug));
      },
      clearCompare() {
        setCompareItems([]);
      }
    };
  }, [compareItems, customer, favorites, isReady]);

  return <UserListsContext.Provider value={value}>{children}</UserListsContext.Provider>;
}

export function useUserLists() {
  const context = useContext(UserListsContext);
  if (!context) {
    throw new Error("useUserLists must be used inside UserListsProvider");
  }

  return context;
}
